import React, { useState, useContext, useMemo } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Typography, COLORS, Breadcrumbs, type BreadcrumbListItem,
  Table, TableContainer, TableHead, TableBody, TableRow, TableCell,
} from 'pvs-design-system';
import { Box, Stack, Divider } from '@mui/material';
import BootConfigContext from 'components/BootConfigContext/BootConfigContext';
import TourModal from 'components/virtual-library/TourModal';
import DeleteResourceModal from 'components/virtual-library/DeleteResourceModal';
import AdvancedSearchFiltersModal from 'components/virtual-library/AdvancedSearchFiltersModal';
import LibraryErrorBoundary from 'components/virtual-library/LibraryErrorBoundary';
import PaginationControls from 'components/virtual-library/PaginationControls';
import DetailsPanel from 'components/virtual-library/DetailsPanel';
import { useGetSubcategoriesQuery, useGetResourcesQuery, useDeleteResourceMutation } from 'api/useLibraryApi';
import { useModalNavigate, useTour } from 'hooks';
import { Notify } from 'components/Notifications/Notifications';
import { extractErrorMessage } from 'utils/errorUtils';
import {
  canManageLibrary,
  canCreateCategory,
  canCreateResource,
  canModifyCategory,
  canDeleteCategory,
  canModifyResource,
  canDeleteResource,
} from 'utils/permissionHelper';
import { encodeQueryValue } from 'utils/urlEncoding';
import { ROUTES } from 'constants/constants';
import { Category, Resource } from 'types';
import LibraryHeader from 'components/virtual-library/LibraryHeader';
import {
  LibraryViewToggle,
  ManageActionsBar,
  LibraryEmptyState,
  LibraryTableSkeleton,
  LibraryGridBarsSkeleton,
  FolderTableRow,
  ResourceTableRow,
  FolderGridCard,
  ResourceGridCard,
  type ViewMode,
} from 'components/virtual-library/library-content';
import LibrarySearchBar, {
  type SearchInValue, type SearchMode,
} from 'components/virtual-library/LibrarySearchBar';

const PAGE_SIZE = 25;

const SubdirectoryPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const lineage = searchParams.get('lineage') ?? '/Home/';
  const navigate = useNavigate();
  const openModal = useModalNavigate();

  const bootConfig = useContext(BootConfigContext);
  const { permissions } = bootConfig.authInfo;
  const canManage = canManageLibrary(permissions);
  const isManageModeActive = canManage;

  const { isTourOpen, tourCompleted, openTour, skipTour, completeTour } = useTour();

  const [searchIn, setSearchIn] = useState<SearchInValue>('all');
  const [searchMode, setSearchMode] = useState<SearchMode>('all');
  const [selectedItem, setSelectedItem] = useState<Resource | null>(null);
  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState<Resource | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [settingsOpen, setSettingsOpen] = useState(false);

  const segments = lineage.split('/').filter(Boolean);
  const categoryName = decodeURIComponent(segments[segments.length - 1] ?? '');

  const breadcrumbLinks = useMemo<BreadcrumbListItem[]>(() => {
    const segs = lineage.split('/').filter(Boolean);
    const items: BreadcrumbListItem[] = [
      { component: RouterLink as React.ElementType, to: ROUTES.library, linkText: 'Virtual Library' },
    ];
    let path = '/';
    for (let i = 0; i < segs.length; i++) {
      path += segs[i] + '/';
      if (segs[i] === 'Home') continue;
      const label = decodeURIComponent(segs[i]);
      const isLast = i === segs.length - 1;
      if (isLast) {
        items.push({ linkText: label });
      } else {
        items.push({
          component: RouterLink as React.ElementType,
          to: `${ROUTES.browse}?lineage=${encodeQueryValue(path)}`,
          linkText: label,
        });
      }
    }
    return items;
  }, [lineage]);

  const { data: subcategories = [], isLoading: isSubcatsLoading, isError: isSubcatsError } = useGetSubcategoriesQuery(lineage, false, isManageModeActive);
  const { data: resourcesData, isLoading: isResourcesLoading, isError: isResourcesError } = useGetResourcesQuery({
    categoryLineage: lineage,
    categoryName,
    isManageMode: isManageModeActive,
  });

  const [deleteResource] = useDeleteResourceMutation();

  const allResources: Resource[] = resourcesData?.items ?? [];
  const totalPages = Math.max(1, Math.ceil(allResources.length / PAGE_SIZE));
  const pagedResources = allResources.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const isLoading = isSubcatsLoading || isResourcesLoading;
  const isDataError = isSubcatsError || isResourcesError;

  const handleSearch = (query: string) => {
    const q = query.trim();
    if (q) {
      const params = new URLSearchParams({ q });
      if (searchIn !== 'all') params.set('searchIn', searchIn);
      if (searchMode !== 'all') params.set('searchMode', searchMode);
      params.set('from', lineage);
      navigate(`${ROUTES.search}?${params.toString()}`);
    }
  };

  const handleFolderClick = (cat: Category, e?: React.MouseEvent) => {
    const childLineage = `${lineage}${cat.name}/`;
    const childUrl = `${ROUTES.browse}?lineage=${encodeQueryValue(childLineage)}`;
    if (e && (e.ctrlKey || e.metaKey)) {
      window.open(childUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    setSelectedItem(null);
    navigate(childUrl);
  };

  const handleResourceClick = (resource: Resource) => {
    window.open(resource.url, '_blank', 'noopener,noreferrer');
  };

  const handleShowDetails = (resource: Resource, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setSelectedItem(resource);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    const { id: deletedId, title } = deleteTarget;
    try {
      await deleteResource(deletedId).unwrap();
      Notify.success(`Resource "${title}" deleted.`);
      if (selectedItem?.id === deletedId) setSelectedItem(null);
    } catch (e) {
      Notify.error(extractErrorMessage(e, 'Failed to delete resource. Please try again.'));
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleEditFolder = (cat: Category) =>
    openModal(ROUTES.editCategory, { editCategory: cat.name, editLineage: lineage });
  const handleDeleteFolder = (cat: Category) =>
    openModal(ROUTES.deleteCategory, { categoryName: cat.name, lineage });
  const handleEditResource = (resource: Resource) =>
    openModal(`${ROUTES.editResource}/${resource.id}`);
  const handleDeleteResource = (resource: Resource) => setDeleteTarget(resource);

  const canEditCategories = isManageModeActive && canModifyCategory(permissions);
  const canDeleteCategories = isManageModeActive && canDeleteCategory(permissions);
  const canEditResources = isManageModeActive && canModifyResource(permissions);
  const canDeleteResources = isManageModeActive && canDeleteResource(permissions);

  return (
    <>
      <Box component="main" sx={{ display: 'flex', flexDirection: 'column', background: COLORS.beige[200] }}>
        <LibraryHeader
          tourCompleted={tourCompleted}
          onTour={openTour}
        />

        {/* Main content: hero + table on the left, details panel on the right */}
        <Box sx={{ display: 'flex' }}>
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            {/* Hero section: search + breadcrumbs (mirrors IndexPage, swapping Recently Visited for breadcrumbs) */}
            <Box>
              <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 6, px: 4, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Typography
                  variant="h2"
                  component="h1"
                  style={{ color: COLORS.gray[900], marginBottom: 20, textAlign: 'left', fontWeight: 700 }}
                >
                  Virtual Library
                </Typography>

                <Box id="home-search-bar" sx={{ width: '100%', maxWidth: 740 }}>
                  <LibrarySearchBar
                    onSearch={handleSearch}
                    size="large"
                    placeholder="Search Virtual Library"
                    settingsOpen={settingsOpen}
                    onSettingsToggle={() => setSettingsOpen((v) => !v)}
                  />
                </Box>
              </Box>

              {/* Breadcrumbs */}
              <Box sx={{ maxWidth: 1100, mx: 'auto', px: 4, mt: 5 }}>
                <Breadcrumbs links={breadcrumbLinks} />
              </Box>
            </Box>

            {/* Browse section */}
            <Box id="browse-section" sx={{ maxWidth: 1100, mx: 'auto', px: 4, mt: 4, mb: 6, width: '100%' }}>
            {/* Manage actions bar + view toggle */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <ManageActionsBar
                canAddFolder={isManageModeActive && canCreateCategory(permissions)}
                canAddResource={isManageModeActive && canCreateResource(permissions)}
                onAddFolder={() => openModal(ROUTES.createCategory, { lineage })}
                onAddResource={() => openModal(ROUTES.createResource, { lineage })}
              />
              <LibraryViewToggle viewMode={viewMode} onChange={setViewMode} />
            </Stack>

            <LibraryErrorBoundary>
              {viewMode === 'list' ? (
                <>
                  <TableContainer sx={{ border: `1px solid ${COLORS.gray[200]}`, borderRadius: 2 }}>
                    <Table
                      stickyHeader
                      size="small"
                      aria-label="Folder contents"
                      sx={{
                        tableLayout: 'fixed',
                        '& .MuiTableCell-root': { paddingTop: '8px', paddingBottom: '8px' },
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell align="center" sx={{ width: 100 }}>Type</TableCell>
                          <TableCell>Name</TableCell>
                          <TableCell sx={{ width: 120 }}>Size</TableCell>
                          <TableCell sx={{ width: 124 }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {isLoading ? (
                          <LibraryTableSkeleton />
                        ) : (
                          <>
                            {subcategories.map((cat) => (
                              <FolderTableRow
                                key={cat.id}
                                folder={cat}
                                onOpen={handleFolderClick}
                                onEdit={handleEditFolder}
                                onDelete={handleDeleteFolder}
                                canEdit={canEditCategories && !!cat.canModify}
                                canDelete={canDeleteCategories && !!cat.canDelete}
                              />
                            ))}

                            {subcategories.length > 0 && pagedResources.length > 0 && (
                              <TableRow>
                                <TableCell colSpan={4} sx={{ p: 0, border: 'none' }}>
                                  <Divider sx={{ borderColor: COLORS.gray[100] }} />
                                </TableCell>
                              </TableRow>
                            )}

                            {pagedResources.map((resource) => (
                              <ResourceTableRow
                                key={resource.id}
                                resource={resource}
                                selected={selectedItem?.id === resource.id}
                                showSelectionBorder
                                onOpen={handleResourceClick}
                                onShowDetails={handleShowDetails}
                                onEdit={handleEditResource}
                                onDelete={handleDeleteResource}
                                canEdit={canEditResources && !!resource.canModify}
                                canDelete={canDeleteResources && !!resource.canDelete}
                              />
                            ))}

                            {subcategories.length === 0 && allResources.length === 0 && (
                              <TableRow>
                                <TableCell colSpan={4} sx={{ border: 'none' }}>
                                  <LibraryEmptyState
                                    isError={isDataError}
                                    message={isDataError ? 'Failed to load folder contents. Please try refreshing.' : 'This folder is empty'}
                                  />
                                </TableCell>
                              </TableRow>
                            )}
                          </>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {!isLoading && totalPages > 1 && (
                    <PaginationControls page={page} totalPages={totalPages} onPageChange={setPage} />
                  )}
                </>
              ) : isLoading ? (
                <LibraryGridBarsSkeleton />
              ) : (
                /* Grid view */
                <>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 1.5, p: 3 }}>
                    {subcategories.map((cat) => (
                      <FolderGridCard
                        key={cat.id}
                        folder={cat}
                        onOpen={handleFolderClick}
                        onEdit={handleEditFolder}
                        onDelete={handleDeleteFolder}
                        canEdit={canEditCategories && !!cat.canModify}
                        canDelete={canDeleteCategories && !!cat.canDelete}
                      />
                    ))}
                    {pagedResources.map((resource) => (
                      <ResourceGridCard
                        key={resource.id}
                        resource={resource}
                        selected={selectedItem?.id === resource.id}
                        showSelection
                        onOpen={handleResourceClick}
                        onShowDetails={handleShowDetails}
                        onEdit={handleEditResource}
                        onDelete={handleDeleteResource}
                        canEdit={canEditResources && !!resource.canModify}
                        canDelete={canDeleteResources && !!resource.canDelete}
                      />
                    ))}
                  </Box>

                  {!isLoading && subcategories.length === 0 && allResources.length === 0 && (
                    <LibraryEmptyState
                      isError={isDataError}
                      message={isDataError ? 'Failed to load folder contents. Please try refreshing.' : 'This folder is empty'}
                    />
                  )}

                  {totalPages > 1 && (
                    <PaginationControls page={page} totalPages={totalPages} onPageChange={setPage} />
                  )}
                </>
              )}
            </LibraryErrorBoundary>
            </Box>
          </Box>

          <DetailsPanel item={selectedItem} onClose={() => setSelectedItem(null)} canViewStatistics={canManage} />
        </Box>
      </Box>

      <TourModal canManage={canManage} isOpen={isTourOpen} onSkip={skipTour} onFinish={completeTour} />
      <DeleteResourceModal open={deleteTarget !== null} resourceTitle={deleteTarget?.title} onConfirm={handleDeleteConfirm} onCancel={() => setDeleteTarget(null)} />
      <AdvancedSearchFiltersModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        searchIn={searchIn}
        onSearchInChange={setSearchIn}
        searchMode={searchMode}
        onSearchModeChange={setSearchMode}
      />
    </>
  );
};

export default SubdirectoryPage;

